import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  fetchTrackerWithEntries,
  updateTrackerAsync,
} from "../store/slices/trackersSlice";
import { trackPageVisit } from "../utils/analytics";
import { DayOfWeek, Tracker, TrackerEntry } from "../types";
import { BarChart, PieChart, LineChart, TimeChart } from "./charts";
import { formatDateToString } from "../utils/dateUtils";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";

const TrackerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<
    "today" | "week" | "month" | "year" | "all"
  >("today");

  const userProfile = useAppSelector((state) => state.user.profile);
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => setTheme(isDark ? "light" : "dark");

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  const {
    items: trackers,
    entries: trackerEntries,
    loading,
  } = useAppSelector((state) => state.trackers);

  const tracker = useMemo(() => {
    return trackers.find((t) => t.id === id);
  }, [trackers, id]);

  const trackerEntriesForTracker = useMemo(() => {
    return trackerEntries.filter((entry) => entry.trackerId === id);
  }, [trackerEntries, id]);

  const total = useMemo(() => {
    return trackerEntriesForTracker.reduce((sum, entry) => {
      if (typeof entry.value === "number") return sum + entry.value;
      if (typeof entry.value === "boolean") return sum + (entry.value ? 1 : 0);
      const num = parseFloat(entry.value);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  }, [trackerEntriesForTracker]);

  // Helper functions for date ranges
  const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const getStartOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getStartOfYear = (date: Date) => {
    return new Date(date.getFullYear(), 0, 1);
  };

  // Compute date ranges
  const now = new Date();
  const today = formatDateToString(now);
  const startOfWeek = formatDateToString(getStartOfWeek(now));
  const startOfMonth = formatDateToString(getStartOfMonth(now));
  const startOfYear = formatDateToString(getStartOfYear(now));

  // Compute totals for different periods
  const periodTotals = useMemo(() => {
    const all = total;
    const todayEntries = trackerEntriesForTracker.filter(
      (entry) => entry.date === today,
    );
    const todayTotal = todayEntries.reduce((sum, entry) => {
      if (typeof entry.value === "number") return sum + entry.value;
      const num = parseFloat(String(entry.value));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
    const weekEntries = trackerEntriesForTracker.filter(
      (entry) => entry.date >= startOfWeek,
    );
    const weekTotal = weekEntries.reduce((sum, entry) => {
      if (typeof entry.value === "number") return sum + entry.value;
      const num = parseFloat(String(entry.value));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
    const monthEntries = trackerEntriesForTracker.filter(
      (entry) => entry.date >= startOfMonth,
    );
    const monthTotal = monthEntries.reduce((sum, entry) => {
      if (typeof entry.value === "number") return sum + entry.value;
      const num = parseFloat(String(entry.value));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
    const yearEntries = trackerEntriesForTracker.filter(
      (entry) => entry.date >= startOfYear,
    );
    const yearTotal = yearEntries.reduce((sum, entry) => {
      if (typeof entry.value === "number") return sum + entry.value;
      const num = parseFloat(String(entry.value));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
    return {
      all,
      today: todayTotal,
      week: weekTotal,
      month: monthTotal,
      year: yearTotal,
    };
  }, [
    trackerEntriesForTracker,
    total,
    today,
    startOfWeek,
    startOfMonth,
    startOfYear,
  ]);

  useEffect(() => {
    trackPageVisit("tracker_detail", {
      tracker_id: id,
      tracker_name: tracker?.name,
      data_type: tracker?.dataType,
    });
  }, [id, tracker]);

  useEffect(() => {
    if (id) {
      // Fetch tracker with entries in a single API call (last 3 months)
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1)
        .toISOString()
        .split("T")[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 31)
        .toISOString()
        .split("T")[0];

      dispatch(fetchTrackerWithEntries({ trackerId: id, startDate, endDate }));
    }
  }, [id, dispatch]);

  const formatTrackingDays = (trackingDays?: DayOfWeek[]) => {
    if (!trackingDays || trackingDays.length === 0) {
      return t("trackerDetail.everyDay");
    }

    const dayLabels: Record<DayOfWeek, string> = {
      monday: t("trackerDetail.dayLabels.monday"),
      tuesday: t("trackerDetail.dayLabels.tuesday"),
      wednesday: t("trackerDetail.dayLabels.wednesday"),
      thursday: t("trackerDetail.dayLabels.thursday"),
      friday: t("trackerDetail.dayLabels.friday"),
      saturday: t("trackerDetail.dayLabels.saturday"),
      sunday: t("trackerDetail.dayLabels.sunday"),
    };

    return trackingDays.map((day) => dayLabels[day]).join(", ");
  };

  const handleBack = () => {
    navigate("/trackers");
  };

  const handleTogglePause = async (isPaused: boolean) => {
    if (!tracker?.id) return;

    try {
      await dispatch(
        updateTrackerAsync({
          id: tracker.id,
          updates: { isPaused },
        }),
      );
    } catch (error) {
      console.error("Error updating tracker pause status:", error);
    }
  };

  if (loading && !tracker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {t("trackerDetail.loadingTrackerDetails")}
          </h2>
        </div>
      </div>
    );
  }

  if (!tracker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-soft mb-6">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {t("trackerDetail.trackerNotFound")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t("trackerDetail.trackerNotFoundMessage")}
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl shadow-soft hover:shadow-glow transition-all duration-200"
          >
            {t("trackerDetail.backToTrackers")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={`${tracker.name} - Tracker - Streak Spark`}
        description={`View detailed information and progress for ${tracker.name} in Streak Spark.`}
        keywords="tracker, details, progress, visualization, data tracking"
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#a33fe8]/20 border border-[#a33fe8]/30 flex items-center justify-center transition-all hover:bg-[#a33fe8]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#a33fe8"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {tracker.name}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label="Toggle theme"
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-[19px] flex items-center justify-center cursor-pointer transition-all"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#a33fe8]/15 border border-[#a33fe8]/25 flex items-center justify-center transition-all hover:bg-[#a33fe8]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#a33fe8"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white flex-1">
              {/* {tracker.name} */}Tracker Details
            </h1>
            <button
              onClick={() => navigate(`/edit-tracker/${tracker.id}`)}
              className="tap-scale px-4 py-2 bg-[#a33fe8]/15 border border-[#a33fe8]/25 text-[#a33fe8] text-[17px] font-medium rounded-xl hover:bg-[#a33fe8]/25 transition-all"
            >
              {t("trackerDetail.edit")}
            </button>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-2xl border border-[#a33fe8]/20 dark:border-[#a33fe8]/30 bg-gradient-to-br from-[#a33fe8]/[0.07] to-transparent dark:from-[#a33fe8]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#a33fe8]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">📊</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {tracker.name}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45">
                        {t("trackerDetail.dataType")}: {tracker.dataType}
                        {tracker.unit ? ` (${tracker.unit})` : ""}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${tracker.isPaused ? "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30" : tracker.isActive ? "bg-[#22c67a]/20 text-[#22c67a] border border-[#22c67a]/30" : "bg-red-500/20 text-red-500 dark:text-red-400 border border-red-500/30"}`}
                  >
                    {tracker.isPaused
                      ? t("editTracker.trackerPaused")
                      : tracker.isActive
                        ? t("trackerDetail.active")
                        : t("trackerDetail.inactive")}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.6] dark:bg-white/[0.04] px-3 py-2">
                    <p className="text-[13px] uppercase tracking-widest text-gray-500 dark:text-white/35">
                      {t("trackerDetail.totalEntries")}
                    </p>
                    <p className="text-[18px] font-semibold tracking-tight text-gray-900 dark:text-white">
                      {trackerEntriesForTracker.length}
                    </p>
                  </div>
                  <div className="rounded-lg border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.6] dark:bg-white/[0.04] px-3 py-2">
                    <p className="text-[13px] uppercase tracking-widest text-gray-500 dark:text-white/35">
                      {t("trackerDetail.trackingDays")}
                    </p>
                    <p className="text-[18px] font-semibold tracking-tight text-gray-900 dark:text-white">
                      {tracker.trackingDays?.length || 7}
                    </p>
                  </div>
                  <div className="rounded-lg border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.6] dark:bg-white/[0.04] px-3 py-2">
                    <p className="text-[13px] uppercase tracking-widest text-gray-500 dark:text-white/35">
                      {t("trackerDetail.totals")}
                    </p>
                    <p className="text-[18px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                      {periodTotals.all.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Info Card */}
              <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-5 lg:p-6 space-y-3">
                <h2 className="text-[17px] font-semibold uppercase tracking-widest text-gray-500 dark:text-white/30">
                  {t("trackerDetail.trackerInformation")}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("trackerDetail.dataType")}
                    </p>
                    <p className="text-[18px] font-semibold text-gray-900 dark:text-white capitalize">
                      {tracker.dataType}
                    </p>
                  </div>
                  {tracker.unit && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                      <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                        {t("trackerDetail.unit")}
                      </p>
                      <p className="text-[18px] font-semibold text-gray-900 dark:text-white">
                        {tracker.unit}
                      </p>
                    </div>
                  )}
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("trackerDetail.trackingDays")}
                    </p>
                    <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                      {formatTrackingDays(tracker.trackingDays)}
                    </p>
                  </div>
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("trackerDetail.status")}
                    </p>
                    <p
                      className={`text-[17px] font-semibold ${tracker.isActive && !tracker.isPaused ? "text-green-600 dark:text-green-400" : "text-orange-500"}`}
                    >
                      {tracker.isPaused
                        ? t("editTracker.trackerPaused")
                        : tracker.isActive
                          ? t("trackerDetail.active")
                          : t("trackerDetail.inactive")}
                    </p>
                  </div>
                </div>
                {tracker.description && (
                  <div className="border-t border-black/[0.06] dark:border-white/[0.07] pt-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-1">
                      {t("trackerDetail.descriptionLabel")}
                    </p>
                    <p className="text-[18px] text-gray-900 dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                      {tracker.description}
                    </p>
                  </div>
                )}
                {tracker.options && tracker.options.length > 0 && (
                  <div className="border-t border-black/[0.06] dark:border-white/[0.07] pt-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-2">
                      {t("trackerDetail.options")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {tracker.options.map((option, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 text-[15px] font-medium bg-[#a33fe8]/15 border border-[#a33fe8]/25 text-[#a33fe8] rounded-full"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Pause/Resume */}
                <div className="border-t border-black/[0.06] dark:border-white/[0.07] pt-3 flex justify-end">
                  {tracker.isPaused ? (
                    <button
                      onClick={() => handleTogglePause(false)}
                      className="tap-scale px-4 py-2 bg-green-500/15 border border-green-500/25 text-green-600 dark:text-green-400 text-[17px] font-medium rounded-xl hover:bg-green-500/25 transition-all"
                    >
                      {t("editTracker.resumeTracker")}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleTogglePause(true)}
                      className="tap-scale px-4 py-2 bg-orange-500/15 border border-orange-500/25 text-orange-600 dark:text-orange-400 text-[17px] font-medium rounded-xl hover:bg-orange-500/25 transition-all"
                    >
                      {t("editTracker.pauseTracker")}
                    </button>
                  )}
                </div>
              </div>

              {/* Data Visualization */}
              <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-5 lg:p-6">
                <h2 className="text-[17px] font-semibold uppercase tracking-widest text-gray-500 dark:text-white/30 mb-4">
                  {t("trackerDetail.dataVisualization")}
                </h2>
                {trackerEntriesForTracker.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 bg-[#a33fe8]/15 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl">📉</span>
                    </div>
                    <p className="text-[18px] font-semibold text-gray-900 dark:text-white mb-1">
                      {t("trackerDetail.noDataEntriesYet")}
                    </p>
                    <p className="text-[16px] text-gray-500 dark:text-white/40">
                      {t("trackerDetail.noDataEntriesMessage")}
                    </p>
                  </div>
                ) : (
                  <TrackerVisualization
                    tracker={tracker}
                    entries={trackerEntriesForTracker}
                  />
                )}
              </div>

              {/* Totals � number trackers only */}
              {tracker.dataType === "number" && (
                <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-5 lg:p-6">
                  <h2 className="text-[17px] font-semibold uppercase tracking-widest text-gray-500 dark:text-white/30 mb-4">
                    {t("trackerDetail.totals")}
                  </h2>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {(["today", "week", "month", "year", "all"] as const).map(
                      (tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 py-1.5 rounded-full text-[16px] font-medium transition-all ${activeTab === tab ? "bg-[#a33fe8]/20 border border-[#a33fe8]/40 text-[#a33fe8]" : "border border-black/[0.07] dark:border-white/[0.08] text-gray-500 dark:text-white/30"}`}
                        >
                          {t(
                            `trackerDetail.${tab === "today" ? "today" : tab === "week" ? "thisWeek" : tab === "month" ? "thisMonth" : tab === "year" ? "thisYear" : "allTime"}`,
                          )}
                        </button>
                      ),
                    )}
                  </div>
                  <div className="text-center py-2">
                    <p className="text-[40px] font-semibold tracking-tight text-[#a33fe8]">
                      {periodTotals[activeTab].toFixed(2)}
                    </p>
                    <p className="text-[16px] text-gray-500 dark:text-white/30 mt-0.5">
                      {tracker.unit || t("trackerDetail.unit")}
                    </p>
                  </div>
                </div>
              )}

              {/* Mobile Edit Button */}
              <div className="lg:hidden">
                <button
                  onClick={() => navigate(`/edit-tracker/${tracker.id}`)}
                  className="w-full py-3 bg-[#a33fe8] text-white text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
                >
                  {t("trackerDetail.edit")}
                </button>
              </div>
            </div>
          </div>

          {/* MOBILE BOTTOM NAV */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 border-t border-black/[0.07] dark:border-white/[0.08] backdrop-blur-sm">
            <div className="flex justify-around px-2 pt-2 pb-5">
              {mobileNavLinks.map(({ path, emoji, label }) => {
                const active = isNavActive(path);
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="flex flex-col items-center gap-1"
                  >
                    <span className="text-[22px]">{emoji}</span>
                    <span
                      className={`text-[13px] font-medium ${active ? "text-[#f5a623]" : "text-gray-400 dark:text-white/25"}`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </main>
      </div>
    </>
  );
};

// Modular chart component for data visualization
const TrackerVisualization: React.FC<{
  tracker: Tracker;
  entries: TrackerEntry[];
}> = ({ tracker, entries }) => {
  const { t } = useTranslation();
  const sortedEntries = useMemo(() => {
    return [...entries].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [entries]);

  // Frequency bar chart for string/options data
  const frequencyMap = useMemo(() => {
    const map: Record<string, number> = {};

    // Initialize all options with 0 count for options datatype
    if (tracker.dataType === "options" && tracker.options) {
      tracker.options.forEach((option) => {
        map[option] = 0;
      });
    }

    // Count actual entries
    sortedEntries.forEach((entry) => {
      const value = String(entry.value);
      map[value] = (map[value] || 0) + 1;
    });
    return map;
  }, [sortedEntries, tracker.dataType, tracker.options]);

  // Pie chart for boolean data
  const booleanStats = useMemo(() => {
    const trueCount = sortedEntries.filter((entry) => {
      if (typeof entry.value === "boolean") return entry.value === true;
      const stringValue = String(entry.value).toLowerCase().trim();
      return (
        stringValue === "true" || stringValue === "yes" || stringValue === "1"
      );
    }).length;

    const falseCount = sortedEntries.filter((entry) => {
      if (typeof entry.value === "boolean") return entry.value === false;
      const stringValue = String(entry.value).toLowerCase().trim();
      return (
        stringValue === "false" || stringValue === "no" || stringValue === "0"
      );
    }).length;

    const total = trueCount + falseCount;

    return {
      trueCount,
      falseCount,
      total,
      pieChartData: [
        {
          label: t("trackerDetail.yes") as string,
          value: trueCount,
          color: "#10b981",
          percentage: total > 0 ? (trueCount / total) * 100 : 0,
        },
        {
          label: t("trackerDetail.no") as string,
          value: falseCount,
          color: "#ef4444",
          percentage: total > 0 ? (falseCount / total) * 100 : 0,
        },
      ].filter((item) => item.value > 0), // Only show items with values
    };
  }, [sortedEntries, t]);

  // Utility function to parse values based on data type
  const parseValue = (
    value: string | number | boolean,
    dataType: string,
  ): number => {
    if (typeof value === "number") return value;
    if (typeof value === "boolean") return value ? 1 : 0;

    const stringValue = String(value).trim();

    switch (dataType) {
      case "number":
        const numValue = parseFloat(stringValue);
        return isNaN(numValue) ? 0 : numValue;

      case "boolean":
        return stringValue.toLowerCase() === "true" ? 1 : 0;

      case "time":
        // Parse time format like "02:30" (HH:MM) to minutes
        const timeMatch = stringValue.match(/^(\d{1,2}):(\d{2})$/);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          return hours * 60 + minutes;
        }
        // Try parsing as decimal hours (e.g., "2.5" for 2.5 hours)
        const decimalHours = parseFloat(stringValue);
        return isNaN(decimalHours) ? 0 : decimalHours * 60;

      default:
        return 0;
    }
  };

  // Line chart for number and time data
  const lineChartData = useMemo(() => {
    if (tracker.dataType === "number") {
      // Aggregate by date for number trackers
      const aggregated: Record<string, number> = {};
      sortedEntries.forEach((entry) => {
        const parsedValue = parseValue(entry.value, tracker.dataType);
        if (!aggregated[entry.date]) aggregated[entry.date] = 0;
        aggregated[entry.date] += parsedValue;
      });
      return Object.entries(aggregated).map(([date, value]) => ({
        date,
        value,
      }));
    } else {
      return sortedEntries.map((entry) => {
        const parsedValue = parseValue(entry.value, tracker.dataType);
        return {
          date: entry.date,
          value: parsedValue,
        };
      });
    }
  }, [sortedEntries, tracker.dataType]);

  if (tracker.dataType === "string" || tracker.dataType === "options") {
    const barChartData = Object.entries(frequencyMap).map(([label, value]) => ({
      label,
      value,
    }));

    const title =
      tracker.dataType === "options"
        ? t("trackerDetail.optionFrequency")
        : t("trackerDetail.valueFrequency");
    return <BarChart data={barChartData} title={title} />;
  }

  if (tracker.dataType === "boolean") {
    return (
      <PieChart
        data={booleanStats.pieChartData}
        title={t("trackerDetail.yesNoDistribution") as string}
        centerValue={booleanStats.total}
      />
    );
  }

  // Use appropriate chart based on data type
  if (tracker.dataType === "time") {
    return (
      <TimeChart
        data={lineChartData}
        title={t("trackerDetail.timeTracking") as string}
        unit={tracker.unit || ""}
      />
    );
  }

  // Default: Line chart for number data
  return (
    <LineChart
      data={lineChartData}
      title={t("trackerDetail.valueOverTime") as string}
      unit={tracker.unit || ""}
      dataType={tracker.dataType}
    />
  );
};

export default TrackerDetail;
